import { memo, useMemo } from 'react';
import { createBrowserHistory } from 'history';
import { Link, useHistory } from 'react-router-dom';
import { IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import type { LinkProps } from 'react-router-dom';
import type { IconButtonProps, Button as MuiButton } from '@mui/material';

const history = createBrowserHistory();

interface BackButtonProps<C = React.ElementType> {
  readonly component?: C;
}

const BaseBackButton = memo<IconButtonProps>(({ ...restProps }) => (
  <IconButton
    id="sda-back-button"
    className="sda-back-button"
    aria-label="back"
    size="medium"
    edge="start"
    sx={{
      mt: '-1px',
    }}
    {...restProps}
  >
    <ArrowBackIcon />
  </IconButton>
));

export const BackButton = <C extends React.ElementType = typeof MuiButton>(
  { component: ButtonComponent = BaseBackButton, ...restProps }: LinkProps & IconButtonProps & BackButtonProps<C> & React.ComponentPropsWithRef<C>,
) => {
  const routerHistory = useHistory();

  const isBackInternal = useMemo(() => {
    if (!history.length || history.length <= 1) {
      return false;
    }

    if (!history.location?.pathname) {
      return false;
    }

    if (!routerHistory.location?.pathname) {
      return false;
    }

    // The browserHistory object will point to the referring page as the location.pathname if the page is internal
    // The routerHistory object will only ever display the current page as the location.pathname
    // Thus, if the two match, it means there was no internal referring page
    return history.location.pathname !== routerHistory.location.pathname;
  }, [routerHistory]);

  if (isBackInternal) {
    return (
      <ButtonComponent onClick={history.goBack} />
    );
  }

  return (
    <ButtonComponent
      component={Link}
      {...restProps}
    />
  );
};
